/**
 * @file ClientStatus.js
 * @description Card for the Client Status dashboard.
 */

import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { DASHBOARD_AS_OF_DATE_FORMAT } from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './ClientStatus.scss';
import { getText } from '../../i18n';

const CLIENT_STATUS_KEYS = Object.freeze({
    InProgress: 'In Progress',
    Active: 'Active',
    Inactive: 'Inactive',
    Demo: 'Demo + Test',
});

const STATUS_TO_BUCKET = Object.freeze({
    1: CLIENT_STATUS_KEYS.Active,
    2: CLIENT_STATUS_KEYS.Demo,
    3: CLIENT_STATUS_KEYS.Demo,
    4: CLIENT_STATUS_KEYS.Inactive,
    5: CLIENT_STATUS_KEYS.Demo,
    6: CLIENT_STATUS_KEYS.InProgress,
    7: CLIENT_STATUS_KEYS.Inactive,
    8: CLIENT_STATUS_KEYS.InProgress,
    9: CLIENT_STATUS_KEYS.InProgress,
});

const CLIENT_STATUS_COLOR_BY_KEY = Object.freeze({
    [CLIENT_STATUS_KEYS.InProgress]: '#FFA800',
    [CLIENT_STATUS_KEYS.Active]: '#8EECA2',
    [CLIENT_STATUS_KEYS.Inactive]: '#82808E',
    [CLIENT_STATUS_KEYS.Demo]: '#17BDBF',
});

function mapApiResponseToClientStatusData(dataFromApi = []) {
    const safeData = Array.isArray(dataFromApi) ? dataFromApi : [];
    const counts = {
        [CLIENT_STATUS_KEYS.InProgress]: 0,
        [CLIENT_STATUS_KEYS.Active]: 0,
        [CLIENT_STATUS_KEYS.Inactive]: 0,
        [CLIENT_STATUS_KEYS.Demo]: 0,
    };

    safeData.forEach(({ status, count }) => {
        const statusKey =
            STATUS_TO_BUCKET[status] || CLIENT_STATUS_KEYS.InProgress;
        counts[statusKey] += Number(count) || 0;
    });

    return [
        {
            name: CLIENT_STATUS_KEYS.InProgress,
            value: counts[CLIENT_STATUS_KEYS.InProgress],
            color: CLIENT_STATUS_COLOR_BY_KEY[CLIENT_STATUS_KEYS.InProgress],
        },
        {
            name: CLIENT_STATUS_KEYS.Active,
            value: counts[CLIENT_STATUS_KEYS.Active],
            color: CLIENT_STATUS_COLOR_BY_KEY[CLIENT_STATUS_KEYS.Active],
        },
        {
            name: CLIENT_STATUS_KEYS.Inactive,
            value: counts[CLIENT_STATUS_KEYS.Inactive],
            color: CLIENT_STATUS_COLOR_BY_KEY[CLIENT_STATUS_KEYS.Inactive],
        },
        {
            name: CLIENT_STATUS_KEYS.Demo,
            value: counts[CLIENT_STATUS_KEYS.Demo],
            color: CLIENT_STATUS_COLOR_BY_KEY[CLIENT_STATUS_KEYS.Demo],
        },
    ];
}

function isChartDataEmpty(data) {
    if (!Array.isArray(data) || data.length === 0) {
        return true;
    }

    return data.every(item => item.value === 0);
}

function ClientStatus({ clientStatusData, endDate }) {
    const [chartData, setChartData] = useState(() =>
        mapApiResponseToClientStatusData(clientStatusData || []),
    );
    const formattedAsOfDate = format(
        new Date(endDate),
        DASHBOARD_AS_OF_DATE_FORMAT,
    );
    const total = chartData.reduce((sum, entry) => sum + entry.value, 0);

    useEffect(() => {
        setChartData(mapApiResponseToClientStatusData(clientStatusData));
    }, [clientStatusData]);

    if (isChartDataEmpty(chartData)) {
        return <NotEnoughData />;
    }

    return (
        <div className="client-status">
            <div className="client-status-date-subtitle">
                {getText('msg_a8770f2d4d18')} {formattedAsOfDate}
            </div>
            <div className="client-status-chart-wrapper">
                <div className="client-status-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (
                                        !active ||
                                        !payload ||
                                        payload.length === 0
                                    ) {
                                        return null;
                                    }

                                    const { name, value } = payload[0].payload;
                                    return (
                                        <CustomDashboardTooltip
                                            active={active}
                                            label={name}
                                            value={value}
                                            unit="clients"
                                        />
                                    );
                                }}
                                wrapperStyle={{ zIndex: 1 }}
                            />
                            <Pie
                                data={chartData}
                                innerRadius={90}
                                outerRadius={115}
                                dataKey="value"
                                startAngle={-180}
                                endAngle={180}
                                stroke="none"
                            >
                                {chartData.map(entry => (
                                    <Cell key={entry.name} fill={entry.color} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="client-status-chart-center">
                        <div className="client-status-total">
                            {total.toLocaleString()}
                        </div>
                        <div className="client-status-label">{getText('msg_5a537e209151')}</div>
                    </div>
                </div>
                <div className="client-status-legend">
                    {chartData.map(entry => {
                        const percentage = total
                            ? ((entry.value / total) * 100).toFixed(1)
                            : '0.0';

                        return (
                            <div
                                key={entry.name}
                                className="client-status-legend-item"
                            >
                                <div
                                    className="client-status-legend-dot"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <div className="client-status-legend-text">
                                    <div className="client-status-legend-label">
                                        {entry.name}
                                    </div>
                                    <div className="client-status-legend-value">
                                        {entry.value.toLocaleString()} (
                                        {percentage}%)
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default ClientStatus;
